import { type LucideIcon, Clock } from 'lucide-react';

import { cn } from '@/nucleo/utilitarios';
import { EmptyState } from '@/ui/EmptyState';

export interface HistoryTimelineEntry {
  id: string;
  icon: LucideIcon;
  /** Classes do círculo do ícone, ex.: `"bg-primary/10 text-primary"`. Sempre token semântico. */
  tone: string;
  /** Rótulo do evento, ex.: "Mudança de status". */
  titulo: string;
  /** Rótulo curto opcional ao lado do título, ex.: o nome do campo alterado. */
  etiqueta?: string;
  /** Data/hora já formatada, pronta pra exibir. */
  quando: string;
  /** Quem fez, quando souber. */
  por?: string | null;
  descricao?: string | null;
  /** Presentes juntos, mostram "de → para". */
  de?: string;
  para?: string;
}

interface HistoryTimelineProps {
  entradas: HistoryTimelineEntry[];
  /** Texto do estado vazio, quando `entradas` está vazio. */
  vazioTitulo?: string;
  vazioDescricao?: string;
}

/**
 * Linha do tempo de eventos (mudança de status, edição, ação relacionada),
 * genérica — quem chama já resolve ícone/tom/texto por evento. Cópia
 * estrutural do protótipo (`OcorrenciaTimeline.tsx`), promovida a componente
 * do design system porque mais de uma tela precisa da mesma peça visual
 * (Ocorrência e, no futuro, Convocação — que hoje não tem timeline por não
 * existir este componente ainda).
 */
export function HistoryTimeline({
  entradas,
  vazioTitulo = 'Sem histórico ainda',
  vazioDescricao = 'As alterações deste registro aparecerão aqui.',
}: HistoryTimelineProps) {
  if (entradas.length === 0) {
    return <EmptyState icon={Clock} title={vazioTitulo} description={vazioDescricao} />;
  }

  return (
    <div className="space-y-0">
      {entradas.map((entrada, indice) => {
        const Icone = entrada.icon;
        const ultima = indice === entradas.length - 1;
        return (
          <div key={entrada.id} className="flex gap-3">
            <div className="flex flex-col items-center">
              <div className={cn('flex h-8 w-8 items-center justify-center rounded-full', entrada.tone)}>
                <Icone className="h-4 w-4" aria-hidden />
              </div>
              {!ultima && <div className="w-px flex-1 bg-border" />}
            </div>

            <div className="flex-1 pb-6">
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">{entrada.titulo}</span>
                {entrada.etiqueta && (
                  <span className="rounded border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground">
                    {entrada.etiqueta}
                  </span>
                )}
              </div>

              <p className="mb-2 text-xs text-muted-foreground">
                {entrada.quando}
                {entrada.por && (
                  <>
                    {' · por '}
                    <span className="font-medium text-foreground">{entrada.por}</span>
                  </>
                )}
              </p>

              {entrada.descricao && <p className="mb-2 text-sm">{entrada.descricao}</p>}

              {entrada.de !== undefined && entrada.para !== undefined && (
                <div className="flex flex-wrap items-center gap-2 rounded-md bg-muted/50 p-2 text-sm">
                  <span className="text-muted-foreground line-through">{entrada.de}</span>
                  <span aria-hidden>→</span>
                  <span className="font-medium">{entrada.para}</span>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default HistoryTimeline;
